import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { useSEO, generateWebPageSchema } from '../../utils/seo';

export default function HowItWorks() {
  // SEO Configuration
  useSEO({
    title: 'How It Works - Rooted & Rising Micro-Habits for Families',
    description: 'Learn how micro-habits transform families through small, intentional actions. Discover the power of habit stacking, atomic habits, and growing together across Faith, Family, Friends, Fitness, Finances, Food, and Fun.',
    keywords: 'how micro-habits work, habit stacking, atomic habits, family transformation, intentional living, small daily actions, family growth system',
    canonical: '/how-it-works',
    schema: generateWebPageSchema({
      name: 'How It Works - Micro-Habits for Family Transformation',
      description: 'Transform your family through the power of micro-habits. Small, intentional actions that compound over time to create lasting change.',
      url: '/how-it-works'
    })
  });

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 bg-gradient-to-br from-amber-50 to-rose-50">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
            How It Works
          </h1>
          <p className="text-xl text-gray-600 leading-relaxed">
            Transform your family through the power of micro-habits. Small, intentional actions that compound over time to create lasting change.
          </p>
        </div>
      </section>

      {/* The Philosophy */}
      <section className="py-24 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-24">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                The Power of Micro-Habits
              </h2>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                A micro-habit is a small action that takes 2-10 minutes but creates compound growth over time. Instead of overwhelming your family with massive changes, we focus on tiny, sustainable actions that naturally become part of your daily rhythm.
              </p>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                When done consistently and together, these small habits create transformation without the overwhelm. They're easy to start, simple to maintain, and powerful in their cumulative effect.
              </p>
              <div className="bg-amber-50 border-l-4 border-amber-500 p-6 rounded-lg">
                <p className="text-gray-700 italic">
                  "We are what we repeatedly do. Excellence, then, is not an act, but a habit." — Aristotle
                </p>
              </div>
            </div>
            <div className="relative h-96 lg:h-full rounded-2xl overflow-hidden">
              <img 
                src="https://public.readdy.ai/ai/img_res/31254d96f78cbabd26913b6b7aecb14b.jpg"
                alt="Building habits together"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>

          {/* Three Core Principles */}
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Three Core Principles
            </h2>
            <p className="text-lg text-gray-600">
              The foundation of the Rooted &amp; Rising approach
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-24">
            <div className="bg-white border-2 border-gray-100 rounded-2xl p-8">
              <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-br from-amber-500 to-orange-500 rounded-full mb-6">
                <span className="text-3xl font-bold text-white">1</span>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Start Atomic</h3>
              <p className="text-gray-600 leading-relaxed">
                Begin with the smallest possible version of a habit. Reading one page, doing one push-up, or sharing one gratitude. Small enough that you can't say no.
              </p>
            </div>

            <div className="bg-white border-2 border-gray-100 rounded-2xl p-8">
              <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-br from-rose-500 to-pink-500 rounded-full mb-6">
                <span className="text-3xl font-bold text-white">2</span>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Stack Strategically</h3>
              <p className="text-gray-600 leading-relaxed">
                Link new habits to existing routines. After breakfast, we share gratitude. After dinner, we take a family walk. Stacking makes habits stick.
              </p>
            </div>

            <div className="bg-white border-2 border-gray-100 rounded-2xl p-8">
              <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-br from-emerald-500 to-teal-500 rounded-full mb-6">
                <span className="text-3xl font-bold text-white">3</span>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Grow Together</h3>
              <p className="text-gray-600 leading-relaxed">
                Shared habits create connection. When families build habits together, they strengthen bonds while growing individually. Everyone rises together.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* The Process */}
      <section className="py-24 px-4 bg-gradient-to-br from-rose-50 to-amber-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Your Journey in Four Steps
            </h2>
            <p className="text-lg text-gray-600">
              A simple framework to get started with micro-habits
            </p>
          </div>

          <div className="space-y-12">
            <div className="flex flex-col md:flex-row gap-8 items-center">
              <div className="flex-shrink-0 w-20 h-20 flex items-center justify-center bg-amber-500 rounded-full">
                <i className="ri-focus-line text-3xl text-white"></i>
              </div>
              <div className="flex-1">
                <h3 className="text-2xl font-bold text-gray-900 mb-3">Step 1: Choose One Pillar</h3>
                <p className="text-lg text-gray-600 leading-relaxed">
                  Don't try to change everything at once. Pick one of the seven pillars (Faith, Family, Friends, Fitness, Finances, Food, or Fun) to focus on first. Start where you feel the most need or excitement.
                </p>
              </div>
            </div>

            <div className="flex flex-col md:flex-row gap-8 items-center">
              <div className="flex-shrink-0 w-20 h-20 flex items-center justify-center bg-rose-500 rounded-full">
                <i className="ri-seedling-line text-3xl text-white"></i>
              </div>
              <div className="flex-1">
                <h3 className="text-2xl font-bold text-gray-900 mb-3">Step 2: Start Micro</h3>
                <p className="text-lg text-gray-600 leading-relaxed">
                  Choose a habit so small it feels almost too easy. Two minutes of prayer, one family hug, five jumping jacks together. The goal is consistency, not intensity. You can always scale up later.
                </p>
              </div>
            </div>

            <div className="flex flex-col md:flex-row gap-8 items-center">
              <div className="flex-shrink-0 w-20 h-20 flex items-center justify-center bg-emerald-500 rounded-full">
                <i className="ri-links-line text-3xl text-white"></i>
              </div>
              <div className="flex-1">
                <h3 className="text-2xl font-bold text-gray-900 mb-3">Step 3: Stack It</h3>
                <p className="text-lg text-gray-600 leading-relaxed">
                  Link your new micro-habit to something you already do every day. "After we brush our teeth, we'll share one thing we're grateful for." This makes the habit automatic and easier to remember.
                </p>
              </div>
            </div>

            <div className="flex flex-col md:flex-row gap-8 items-center">
              <div className="flex-shrink-0 w-20 h-20 flex items-center justify-center bg-violet-500 rounded-full">
                <i className="ri-line-chart-line text-3xl text-white"></i>
              </div>
              <div className="flex-1">
                <h3 className="text-2xl font-bold text-gray-900 mb-3">Step 4: Track &amp; Expand</h3>
                <p className="text-lg text-gray-600 leading-relaxed">
                  Celebrate small wins and track your consistency. After 30 days, you can expand the habit, add a new one, or move to another pillar. Build momentum slowly and sustainably.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Example Habits */}
      <section className="py-24 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Example Micro-Habits
            </h2>
            <p className="text-lg text-gray-600">
              Simple actions across all seven pillars
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {exampleHabits.map((habit, index) => (
              <div key={index} className="bg-white border-2 border-gray-100 rounded-xl p-6 hover:border-amber-200 transition-colors">
                <div className={`inline-block px-3 py-1 ${habit.color} rounded-full text-sm font-medium mb-4`}>
                  {habit.pillar}
                </div>
                <h4 className="text-lg font-semibold text-gray-900 mb-2">{habit.title}</h4>
                <p className="text-gray-600 text-sm">{habit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-4 bg-gradient-to-br from-amber-500 to-rose-500">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Ready to Get Started?
          </h2>
          <p className="text-xl text-white/90 mb-8">
            Explore the seven pillars and discover micro-habits that will transform your family
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/pillars" 
              className="px-8 py-4 bg-white text-gray-900 rounded-full font-medium hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer"
            >
              Explore the Pillars
            </Link>
            <Link 
              to="/blog" 
              className="px-8 py-4 bg-gray-900 text-white rounded-full font-medium hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer"
            >
              Read the Blog
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

const exampleHabits = [
  {
    pillar: "Faith",
    title: "2-Minute Morning Prayer",
    description: "Start each day with a brief family prayer of gratitude",
    color: "bg-amber-100 text-amber-800"
  },
  {
    pillar: "Family",
    title: "Daily Family Hug",
    description: "One intentional group hug before everyone leaves for the day",
    color: "bg-rose-100 text-rose-800"
  },
  {
    pillar: "Friends",
    title: "Weekly Connection Text",
    description: "Send one meaningful message to a friend each week",
    color: "bg-emerald-100 text-emerald-800"
  },
  {
    pillar: "Fitness",
    title: "5-Minute Family Stretch",
    description: "Simple stretching routine together after dinner",
    color: "bg-cyan-100 text-cyan-800"
  },
  {
    pillar: "Finances",
    title: "Gratitude Jar Savings",
    description: "Add one dollar to savings jar when sharing gratitude",
    color: "bg-violet-100 text-violet-800"
  },
  {
    pillar: "Food",
    title: "One Veggie Challenge",
    description: "Try one new vegetable together each week",
    color: "bg-orange-100 text-orange-800"
  },
  {
    pillar: "Fun",
    title: "10-Minute Game Night",
    description: "Play one quick game together before bedtime",
    color: "bg-pink-100 text-pink-800"
  },
  {
    pillar: "Faith",
    title: "Bedtime Blessing",
    description: "Share one blessing from the day before sleep",
    color: "bg-amber-100 text-amber-800"
  },
  {
    pillar: "Family",
    title: "Dinner Table Check-In",
    description: "Each person shares their high and low of the day",
    color: "bg-rose-100 text-rose-800"
  }
];